import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, filter, finalize, Observable, take } from 'rxjs';
import {
  CODOutstandingReportRequest,
  CODOutstandingReportResponse,
} from 'src/app/core/models/reports/cod-outstanding-report.model';
import {
  CODReconcileReportRequest,
  CODReconcileReportResponse,
  CODReconcileRoutes,
} from 'src/app/core/models/reports/cod-reconcile-report.model';
import {
  CODReconcileRequest,
  CODReconcileResponse,
  EditCODReconcileRequest,
  EditCODReconcileResponse,
} from 'src/app/core/models/reports/cod-reconcile.model';
import {
  GroupCodExtractRequest,
  GroupCodExtractResponse,
} from 'src/app/core/models/reports/group-cod-extract.model';
import {
  CODCollectedReportRequest,
  CODCollectedReportResponse,
  CODOutForDeliveryReportRequest,
  CODOutForDeliveryReportResponse,
} from 'src/app/core/models/reports/reports.model';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { environment } from 'src/environments/environment';
import { ErrorhandleService } from '../../error-handling/error-handle/errorhandle.service';

@Injectable({
  providedIn: 'root',
})
export class CodsReportsService {
  baseUrl = environment.apiUrl.concat('report-management/');

  constructor(
    private readonly http: HttpClient,
    private readonly commonUtils: CommonUtils,
    private readonly errorHandler: ErrorhandleService,
  ) {}

  /**
   * To get COD reconcile routes for given location code
   *
   * @param {string} locationCode
   * @return {*}  {Observable<CODReconcileRoutes>}
   * @memberof CodsReportsService
   */
  getReconcileRoutes(locationCode: string): Observable<CODReconcileRoutes> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .get<CODReconcileRoutes>(
        this.baseUrl.concat('COD-reconcile-report/routes'),
        {
          params: { locationCode: locationCode },
        },
      )
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<CODReconcileRoutes>(
            error,
            error.status,
            'Report Service: Get COD Reconcile Routes',
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   * To get COD Reconcile Report for given route
   *
   * @param {CODReconcileReportRequest} request
   * @return {*}  {Observable<CODReconcileReportResponse>}
   * @memberof CodsReportsService
   */
  getCODReconcileReport(
    request: CODReconcileReportRequest,
  ): Observable<CODReconcileReportResponse> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .get<CODReconcileReportResponse>(
        this.baseUrl.concat('COD-reconcile-report'),
        {
          params: { ...request },
        },
      )
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<CODReconcileReportResponse>(
            error,
            error.status,
            'Report Service: Get COD Reconcile Report',
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   * To get COD Outstanding Report for given route
   *
   * @param {CODOutstandingReportRequest} request
   * @return {*}  {Observable<CODOutstandingReportResponse>}
   * @memberof CodsReportsService
   */
  getCODOutstandingReport(
    request: CODOutstandingReportRequest,
  ): Observable<CODOutstandingReportResponse> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .get<CODOutstandingReportResponse>(
        this.baseUrl.concat('COD-uncollected-uncleared-report'),
        {
          params: { ...request },
        },
      )
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<CODOutstandingReportResponse>(
            error,
            error.status,
            'Report Service: Get COD Outstanding Report',
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   * To get COD Reconcile
   *
   * @param {CODReconcileRequest} request
   * @return {*}  {Observable<CODReconcileResponse>}
   * @memberof CodsReportsService
   */
  getCODReconcile(
    request: CODReconcileRequest,
  ): Observable<CODReconcileResponse> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .get<CODReconcileResponse>(this.baseUrl.concat('get-all-COD-reconcile'), {
        params: { ...request },
      })
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<CODReconcileResponse>(
            error,
            error.status,
            'Report Service: Get COD Reconcile',
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   *
   * To get Group COD Extract Report
   * @param {GroupCodExtractRequest} request
   * @return {*}  {Observable<GroupCodExtractResponse>}
   * @memberof CodsReportsService
   */
  getGroupCODExtractReport(
    request: GroupCodExtractRequest,
  ): Observable<GroupCodExtractResponse> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .get<GroupCodExtractResponse>(
        this.baseUrl.concat('group-COD-extract-report'),
        {
          params: { ...request },
        },
      )
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<GroupCodExtractResponse>(
            error,
            error.status,
            'Report Service: Get Group COD Extract Report',
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   *
   * To get COD Collected Report
   * @param {CODCollectedReportRequest} request
   * @return {*}  {Observable<CODCollectedReportResponse>}
   * @memberof CodsReportsService
   */
  getCODCollectedReport(
    request: CODCollectedReportRequest,
  ): Observable<CODCollectedReportResponse> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .get<CODCollectedReportResponse>(
        this.baseUrl.concat('COD-collected-cleared-report'),
        {
          params: { ...request },
        },
      )
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<CODCollectedReportResponse>(
            error,
            error.status,
            'Report Service: Get COD Collected Report',
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   *
   * To get CODoutfordelivery Report
   * @param {CODOutForDeliveryReportRequest} request
   * @return {*}  {Observable<CODOutForDeliveryReportResponse>}
   * @memberof CodsReportsService
   */
  getCODOutForDeliveryReport(
    request: CODOutForDeliveryReportRequest,
  ): Observable<CODOutForDeliveryReportResponse> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .get<CODOutForDeliveryReportResponse>(
        this.baseUrl.concat('CODoutfordelivery-report'),
        {
          params: { ...request },
        },
      )
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<CODOutForDeliveryReportResponse>(
            error,
            error.status,
            'Report Service: Get COD OutForDelivery Report',
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   * To edit a COD Reconcile
   *
   * @param {string} locationCode
   * @param {EditCODReconcileRequest} requestBody
   * @return {*}  {Observable<EditCODReconcileResponse>}
   * @memberof CodsReportsService
   */
  editCodReconcile(
    locationCode: string,
    requestBody: EditCODReconcileRequest,
  ): Observable<EditCODReconcileResponse> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .put<EditCODReconcileResponse>(
        `${this.baseUrl.concat(
          'edit-cod-reconcile',
        )}?locationcode=${locationCode}`,
        requestBody,
      )
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<EditCODReconcileResponse>(
            error,
            error.status,
            'COD Reconcile Report Service: Edit COD Reconcile',
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
        filter((value) => !!value),
      );
  }
}
